import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosClient, { linkImage } from "../../axios-client";
import { useStateContext } from "../../context/ContextProvider";
import { useTranslation } from "react-i18next";
import LanguageChanger from "../../components/language/LanguageChanger";
import { Avatar, Menu, MenuButton, MenuList, MenuItem, IconButton, Flex, Box } from "@chakra-ui/react";
import { FiMoreVertical } from "react-icons/fi";

// ─── Currency colours ──────────────────────────────────────────────────────────
const CUR_COLORS = {
  MAD: "#10b981", USD: "#3b82f6", EUR: "#8b5cf6",
  GBP: "#f59e0b", AED: "#06b6d4", SAR: "#ef4444",
  DZD: "#84cc16", TND: "#f97316", EGP: "#ec4899",
};

// ─── SVG Icons ─────────────────────────────────────────────────────────────────
const BuildingIcon = ({ size = 28 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width={size} height={size}>
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <path d="M9 22V12h6v10M9 7h.01M15 7h.01M9 11h.01M15 11h.01" strokeLinecap="round" />
  </svg>
);
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
  </svg>
);
const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
    <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
    <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const LockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" />
  </svg>
);

// ─── Roles that are sub-users (not the owner) ──────────────────────────────────
const SUB_USER_ROLES = ["ManagerClient", "RespoSuiviClient", "StockManagerClient"];
const ADMIN_CLIENT_ROLE = "AdminClient";

export default function BusinessSelection() {
  const navigate = useNavigate();
  const { user, setToken, setUser, setCurrentBusiness, currentBusiness } = useStateContext();

  const { t } = useTranslation()
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);

  const showToast = (msg, type = "success") => {
    setToastMsg({ msg, type });
    setTimeout(() => setToastMsg(null), 3500);
  };

  const isAdminClient = user?.role === ADMIN_CLIENT_ROLE;
  const isSubUser = SUB_USER_ROLES.includes(user?.role);

  // ── Fetch businesses ───────────────────────────────────────────────────────
  // The server scopes by the authenticated user — no need to pass owner param.
  // AdminClient → their own businesses, Sub-users → only businesses they're assigned to.
  const fetchBusinesses = useCallback(() => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ page_size: 50 });

    axiosClient
      .get(`/businesses/all/?${params}`)
      .then((r) => {
        const list = r.data?.results ?? r.data;
        setBusinesses(Array.isArray(list) ? list.filter((b) => !b.is_deleted) : []);
      })
      .catch((err) => {
        // // console.error("Failed to fetch businesses:", err);
        setError(t("Impossible de charger vos espaces de travail. Vérifiez votre connexion."));
        setBusinesses([]);
      })
      .finally(() => setLoading(false));
  }, [user?.id]);

  // ── Fetch subscription (for AdminClient limit display only) ────────────────
  useEffect(() => {
    if (!user?.id || !isAdminClient) return;
    axiosClient
      .get(`/check-subscription-status/?client=${user.id}`)
      .then((r) => setSubscription(r.data))
      .catch(() => { });
  }, [user?.id, isAdminClient]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  const handleSelect = (biz) => {
    if (!biz.is_active) return;
    setCurrentBusiness(biz);
    navigate("/");
  };

  const handleLogout = () => {
    setUser({});
    setToken(null);
    localStorage.removeItem("tokenExpirationTime");
    localStorage.removeItem("refreshTimeAdmin");
    localStorage.removeItem("role");
    navigate("/login");
  };

  const isSubExpired = subscription?.has_subscription && !subscription?.is_active;
  const realMaxBiz = subscription?.subscription?.plan_max_businesses ?? null;
  const maxAllowedActive = isSubExpired ? 1 : (realMaxBiz === 0 ? Infinity : realMaxBiz);

  const activeCount = businesses.filter(b => b.is_active).length;

  useEffect(() => {
    if (!loading && isAdminClient && businesses.length > 0 && subscription) {
      const activeBizs = businesses.filter(b => b.is_active);
      if (maxAllowedActive !== null && activeBizs.length > maxAllowedActive) {
        const toDeactivate = activeBizs.slice(maxAllowedActive);

        if (currentBusiness) {
          const isCurrentDeactivated = toDeactivate.some(b => b.id === currentBusiness.id);
          if (isCurrentDeactivated) {
            setCurrentBusiness(null);
          }
        }

        const deactivatePromises = toDeactivate.map(b =>
          axiosClient.post(`/toggle-business-status/${b.id}/`)
        );
        Promise.all(deactivatePromises).then(() => {
          fetchBusinesses();
        });
      }
    }
  }, [loading, businesses, subscription, isAdminClient, fetchBusinesses, maxAllowedActive, currentBusiness, setCurrentBusiness]);

  const handleToggleActive = (biz) => {
    if (!biz.is_active && maxAllowedActive !== null && activeCount >= maxAllowedActive) {
      showToast(`${t("Vous avez atteint la limite de {{max}} business actif(s).", { max: maxAllowedActive === Infinity ? t("illimité") : maxAllowedActive })} ${isSubExpired ? t("Votre abonnement est expiré.") : t("Veuillez mettre à niveau votre plan.")}`, "error");
      return;
    }
    axiosClient.post(`/toggle-business-status/${biz.id}/`)
      .then(() => {
        if (biz.is_active && currentBusiness?.id === biz.id) {
          setCurrentBusiness(null);
        }
        fetchBusinesses();
      })
      .catch(() => { });
  };

  const maxBiz = realMaxBiz;
  const canCreate = isAdminClient && (!maxBiz || businesses.length < (maxBiz === 0 ? Infinity : maxBiz));

  // ── Greeting based on role ─────────────────────────────────────────────────
  const subtitle = isAdminClient
    ? t("Choisissez un espace de travail pour continuer")
    : t("Sélectionnez un business auquel vous avez accès");

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #110a29 0%, #5A3B8A 60%, #7B5EA7 100%)",
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "flex-start", padding: "0 16px 40px",
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    }}>

      {/* ── Toast ─────────────────────────────────────────────────────────── */}
      {toastMsg && (
        <div style={{
          position: "fixed", top: 20, right: 24, zIndex: 9999,
          background: toastMsg.type === "error" ? "#ef4444" : "#10b981",
          color: "#fff", padding: "12px 20px", borderRadius: 8,
          boxShadow: "0 10px 25px rgba(0,0,0,.2)",
          fontWeight: 500, fontSize: 14, maxWidth: 360,
        }}>
          {toastMsg.msg}
        </div>
      )}

      {/* ── Topbar ─────────────────────────────────────────────────────────── */}
      <div style={{
        width: "100%", maxWidth: 920,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "28px 0 0",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: "50%",
            background: "rgba(255,255,255,.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 16, overflow: "hidden",
          }}>
            {user?.image
              ? <Avatar size={"full"} src={linkImage + user.image} />
              : (user?.first_name?.[0] || "U").toUpperCase()}
          </div>
          <div>
            <div style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>
              {user?.first_name} {user?.last_name}
            </div>
            <div style={{ color: "rgba(255,255,255,.5)", fontSize: 12 }}>
              {user?.email}
              {isSubUser && (
                <span style={{
                  marginLeft: 8, padding: "1px 8px",
                  background: "rgba(139,92,246,.3)", borderRadius: 99,
                  color: "#c4b5fd", fontSize: 10, fontWeight: 600,
                }}>
                  {(user.role === "Admin" || user.role === "AdminClient")
                    ? t("Administrateur")
                    : (user.role === "Manager" || user.role === "ManagerClient")
                      ? t("Manager")
                      : (user.role === "RespoSuivi" || user.role === "RespoSuiviClient")
                        ? t("Centre d'appel")
                        : (user.role === "StockManager" || user.role === "StockManagerClient")
                          ? t("Gestionnaire de stock")

                          : ""}
                </span>
              )}
            </div>
          </div>
        </div>
        <Box>
          {/* Desktop View */}
          <Flex display={{ base: "none", sm: "flex" }} gap="10px" alignItems="center">
            <LanguageChanger color={"white"} />
            {isAdminClient && (
              <button
                onClick={() => navigate("/mes-businesses")}
                style={{
                  display: "flex", alignItems: "center", gap: 7,
                  padding: "8px 16px", background: "rgba(255,255,255,.08)",
                  color: "#c4b5fd", border: "1px solid rgba(139,92,246,.4)",
                  borderRadius: 9, cursor: "pointer", fontSize: 13, fontWeight: 500,
                }}>
                {t("Gérer")}
              </button>
            )}
            <button
              onClick={handleLogout}
              style={{
                display: "flex", alignItems: "center", gap: 7,
                padding: "8px 16px", background: "rgba(255,255,255,.1)",
                color: "#fff", border: "1px solid rgba(255,255,255,.2)",
                borderRadius: 9, cursor: "pointer", fontSize: 13, fontWeight: 500,
              }}>
              <LogoutIcon /> {t("Déconnexion")}
            </button>
          </Flex>

          {/* Mobile View */}
          <Flex display={{ base: "flex", sm: "none" }} gap="8px" alignItems="center">
            <LanguageChanger color={"white"} />
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<FiMoreVertical />}
                variant="outline"
                color="white"
                borderColor="rgba(255, 255, 255, 0.2)"
                bg="rgba(255, 255, 255, 0.1)"
                _hover={{ bg: "rgba(255, 255, 255, 0.2)" }}
                _active={{ bg: "rgba(255, 255, 255, 0.25)" }}
                _focus={{ bg: "rgba(255, 255, 255, 0.25)", boxShadow: "none" }}
                size="sm"
                rounded="lg"
                aria-label={t("Options")}
              />
              <MenuList bg="#1a0a3e" borderColor="rgba(255, 255, 255, 0.15)" boxShadow="xl" rounded="xl" p={1} zIndex={1000}>
                {isAdminClient && (
                  <MenuItem
                    icon={<BuildingIcon size={16} />}
                    onClick={() => navigate("/mes-businesses")}
                    bg="transparent"
                    color="#c4b5fd"
                    _hover={{ bg: "whiteAlpha.200", color: "#c4b5fd" }}
                    _focus={{ bg: "whiteAlpha.200", color: "#c4b5fd" }}
                    _active={{ bg: "whiteAlpha.200", color: "#c4b5fd" }}
                    rounded="lg"
                    fontSize="sm"
                    fontWeight="500"
                  >
                    {t("Gérer")}
                  </MenuItem>
                )}
                <MenuItem
                  icon={<LogoutIcon />}
                  onClick={handleLogout}
                  bg="transparent"
                  color="#ef4444"
                  _hover={{ bg: "whiteAlpha.200", color: "#ef4444" }}
                  _focus={{ bg: "whiteAlpha.200", color: "#ef4444" }}
                  _active={{ bg: "whiteAlpha.200", color: "#ef4444" }}
                  rounded="lg"
                  fontSize="sm"
                  fontWeight="500"
                >
                  {t("Déconnexion")}
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        </Box>
      </div>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <div style={{ textAlign: "center", padding: "48px 0 36px" }}>
        <div style={{
          width: 72, height: 72, borderRadius: 20, margin: "0 auto 20px",
          background: "rgba(255,255,255,.12)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff",
        }}>
          <BuildingIcon size={28} />
        </div>
        <h1 style={{
          color: "#fff", fontSize: 32, fontWeight: 800,
          margin: "0 0 12px", letterSpacing: "-0.5px",
        }}>
          {t("Sélectionnez votre Business")}
        </h1>
        <p style={{ color: "rgba(255,255,255,.6)", fontSize: 15, margin: 0 }}>
          {subtitle}
        </p>

        {/* Subscription limit indicator — AdminClient only */}
        {isAdminClient && subscription && maxBiz !== null && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8, marginTop: 16,
            background: "rgba(255,255,255,.1)", borderRadius: 99, padding: "6px 16px",
          }}>
            <span style={{ color: "rgba(255,255,255,.6)", fontSize: 12 }}>{t("Plan:")}</span>
            <span style={{ color: "#c4b5fd", fontWeight: 600, fontSize: 12 }}>{subscription.subscription?.plan_name}</span>
            <span style={{ color: "rgba(255,255,255,.4)", fontSize: 12 }}>•</span>
            <span style={{
              color: activeCount >= maxAllowedActive ? "#fca5a5" : "#86efac",
              fontSize: 12, fontWeight: 600
            }}>
              {activeCount}/{maxAllowedActive === Infinity ? "∞" : maxAllowedActive} {t("actifs")}
            </span>
            <span style={{ color: "rgba(255,255,255,.4)", fontSize: 12 }}>•</span>
            <span style={{
              color: businesses.length >= (maxBiz === 0 ? Infinity : maxBiz) ? "#fca5a5" : "#86efac",
              fontSize: 12, fontWeight: 600
            }}>
              {businesses.length}/{maxBiz === 0 ? "∞" : maxBiz} {t("créés")}
            </span>
          </div>
        )}

        {/* Sub-user access notice */}
        {isSubUser && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8, marginTop: 16,
            background: "rgba(255,255,255,.08)", borderRadius: 12, padding: "10px 18px",
            border: "1px solid rgba(255,255,255,.15)",
          }}>
            <LockIcon />
            <span style={{ color: "rgba(255,255,255,.7)", fontSize: 13 }}>
              {t("Vous avez accès uniquement aux businesses auxquels vous êtes assigné.")}
            </span>
          </div>
        )}
      </div>

      {/* ── Business Grid ──────────────────────────────────────────────────── */}
      <div style={{ width: "100%", maxWidth: 920 }}>

        {/* Error state */}
        {error && !loading && (
          <div style={{
            textAlign: "center", padding: "40px 24px",
            background: "rgba(239,68,68,.12)", borderRadius: 20,
            border: "1px solid rgba(239,68,68,.3)", marginBottom: 20,
          }}>
            <p style={{ color: "#fca5a5", fontWeight: 600, margin: "0 0 16px" }}>{error}</p>
            <button
              onClick={fetchBusinesses}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "10px 20px", background: "rgba(255,255,255,.1)",
                color: "#fff", border: "1px solid rgba(255,255,255,.3)",
                borderRadius: 10, cursor: "pointer", fontWeight: 600, fontSize: 14,
              }}>
              <RefreshIcon /> {t("Réessayer")}
            </button>
          </div>
        )}

        {/* Loading skeletons */}
        {loading && (
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 16,
          }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{
                height: 180, borderRadius: 20,
                background: "rgba(255,255,255,.08)",
                animation: "shimmer 1.5s infinite",
              }} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && businesses.length === 0 && (
          <div style={{
            textAlign: "center", padding: "60px 24px",
            background: "rgba(255,255,255,.07)", borderRadius: 24,
            border: "1px dashed rgba(255,255,255,.2)",
          }}>
            <div style={{ color: "rgba(255,255,255,.3)", marginBottom: 16 }}><BuildingIcon size={40} /></div>
            {isAdminClient ? (
              <>
                <p style={{ color: "rgba(255,255,255,.7)", fontWeight: 600, fontSize: 18, margin: "0 0 8px" }}>
                  {t("Aucun business créé")}
                </p>
                <p style={{ color: "rgba(255,255,255,.4)", fontSize: 14, margin: "0 0 24px" }}>
                  {t("Créez votre premier espace de travail pour commencer")}
                </p>
                {canCreate && (
                  <button
                    onClick={() => navigate("/ajouter-business")}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 8,
                      padding: "12px 24px",
                      background: "linear-gradient(135deg, #7B5EA7, #9333ea)",
                      color: "#fff", border: "none", borderRadius: 12,
                      fontWeight: 700, cursor: "pointer", fontSize: 14,
                      boxShadow: "0 8px 24px rgba(124,58,237,.4)",
                    }}>
                    <PlusIcon /> {t("Créer un Business")}
                  </button>
                )}
              </>
            ) : (
              <>
                <p style={{ color: "rgba(255,255,255,.7)", fontWeight: 600, fontSize: 18, margin: "0 0 8px" }}>
                  {t("Aucun business accessible")}
                </p>
                <p style={{ color: "rgba(255,255,255,.4)", fontSize: 14, margin: "0 0 8px" }}>
                  {t("Vous n'avez pas encore été assigné à un espace de travail.")}
                </p>
                <p style={{ color: "rgba(255,255,255,.3)", fontSize: 13, margin: 0 }}>
                  {t("Contactez votre administrateur pour obtenir l'accès.")}
                </p>
              </>
            )}
          </div>
        )}

        {/* Business Cards */}
        {!loading && !error && businesses.length > 0 && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))",
            gap: 16,
          }}>
            {businesses.map((biz) => (
              <BusinessCard
                key={biz.id}
                biz={biz}
                isActive={hoveredId === biz.id}
                onHover={() => setHoveredId(biz.id)}
                onLeave={() => setHoveredId(null)}
                onClick={() => handleSelect(biz)}
                isCurrent={currentBusiness?.id === biz.id}
                isAdminClient={isAdminClient}
                onToggleActive={handleToggleActive}
                disabledToggle={maxAllowedActive !== null && activeCount >= maxAllowedActive}
              />
            ))}

            {/* Add new business card — AdminClient only */}
            {isAdminClient && (
              <button
                onClick={() =>
                  canCreate
                    ? navigate("/ajouter-business")
                    : showToast(t("Limite atteinte. Veuillez mettre à niveau votre plan."), "error")
                }
                style={{
                  height: 180, borderRadius: 20,
                  background: "rgba(255,255,255,.05)",
                  border: "2px dashed rgba(255,255,255,.2)",
                  color: canCreate ? "rgba(255,255,255,.6)" : "rgba(255,255,255,.3)",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", gap: 10,
                  cursor: canCreate ? "pointer" : "not-allowed",
                  transition: "all .2s", fontSize: 14, fontWeight: 500,
                }}
                onMouseEnter={(e) => { if (canCreate) e.currentTarget.style.background = "rgba(255,255,255,.1)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,.05)"; }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: "50%",
                  background: "rgba(255,255,255,.1)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <PlusIcon />
                </div>
                {canCreate ? t("Nouveau Business") : t("Limite atteinte")}
              </button>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes shimmer {
          0%, 100% { opacity: .6; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ─── Single Business Card ──────────────────────────────────────────────────────
function BusinessCard({ biz, isActive, onHover, onLeave, onClick, isCurrent, isAdminClient, onToggleActive, disabledToggle }) {
  const { t } = useTranslation();
  const cc = CUR_COLORS[biz.currency] || "#7B5EA7";
  const inactive = !biz.is_active;

  return (
    <div
      onClick={!inactive ? onClick : undefined}
      onMouseEnter={!inactive ? onHover : undefined}
      onMouseLeave={!inactive ? onLeave : undefined}
      style={{
        borderRadius: 20, padding: "24px", cursor: inactive ? "not-allowed" : "pointer",
        background: isCurrent
          ? "rgba(124,58,237,.35)"
          : isActive
            ? "rgba(255,255,255,.18)"
            : "rgba(255,255,255,.10)",
        backdropFilter: "blur(10px)",
        border: isCurrent
          ? "1px solid rgba(196,181,253,.6)"
          : `1px solid ${isActive ? "rgba(255,255,255,.4)" : "rgba(255,255,255,.15)"}`,
        transform: isActive ? "translateY(-3px) scale(1.01)" : "translateY(0) scale(1)",
        transition: "all .2s ease",
        opacity: inactive ? 0.45 : 1,
        display: "flex", flexDirection: "column", gap: 14, position: "relative",
        boxShadow: isActive ? "0 16px 40px rgba(0,0,0,.3)" : "0 4px 16px rgba(0,0,0,.15)",
      }}
    >
      {/* Status badges & Toggle */}
      <div style={{ position: "absolute", top: 14, right: 14, display: "flex", gap: 6, alignItems: "center" }}>
        {isAdminClient && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              if (!disabledToggle || biz.is_active) {
                onToggleActive(biz);
              }
            }}
            title={disabledToggle && !biz.is_active ? t("Limite d'activation atteinte") : (biz.is_active ? t("Désactiver") : t("Activer"))}
            style={{
              width: 40, height: 22, borderRadius: 12,
              background: biz.is_active ? "#10b981" : "rgba(255,255,255,.2)",
              position: "relative",
              cursor: (disabledToggle && !biz.is_active) ? "not-allowed" : "pointer",
              opacity: (disabledToggle && !biz.is_active) ? 0.5 : 1,
              transition: "background 0.3s ease",
            }}
          >
            <div style={{
              width: 18, height: 18, borderRadius: "50%", background: "#fff",
              position: "absolute", top: 2, left: biz.is_active ? 20 : 2,
              transition: "left 0.3s ease",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
            }} />
          </div>
        )}

        {inactive && !isAdminClient && (
          <div style={{
            background: "#ef4444", color: "#fff",
            fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
          }}>
            {t("INACTIF")}
          </div>
        )}
      </div>

      {/* Logo + name row */}
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        {biz.logo ? (
          <img src={`${biz.logo}`} alt={biz.name}
            style={{ width: 48, height: 48, borderRadius: 14, objectFit: "cover", flexShrink: 0 }} />
        ) : (
          <div style={{
            width: 48, height: 48, borderRadius: 14, flexShrink: 0,
            background: "linear-gradient(135deg, #7B5EA7, #9333ea)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 20,
          }}>
            {biz.name.charAt(0).toUpperCase()}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            color: "#fff", fontWeight: 700, fontSize: 16,
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {biz.name}
          </div>
          <div style={{
            display: "inline-block", marginTop: 4, padding: "1px 10px",
            background: cc + "30", color: cc,
            borderRadius: 99, fontSize: 11, fontWeight: 700,
          }}>
            {biz.currency}
          </div>
        </div>
        {!inactive && (
          <div style={{ color: "rgba(255,255,255,.5)", flexShrink: 0 }}>
            <ChevronRight />
          </div>
        )}
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 10 }}>
        {[
          { label: t("Commandes"), val: biz.orders_count ?? 0 },
          { label: t("Produits"), val: biz.products_count ?? 0 },
        ].map(({ label, val }) => (
          <div key={label} style={{
            flex: 1, background: "rgba(255,255,255,.08)",
            borderRadius: 10, padding: "8px 12px", textAlign: "center",
          }}>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>{val}</div>
            <div style={{ color: "rgba(255,255,255,.45)", fontSize: 10, marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Inactive overlay message */}
      {inactive && (
        <div style={{
          fontSize: 12, color: "rgba(255,255,255,.4)", textAlign: "center",
          borderTop: "1px solid rgba(255,255,255,.1)", paddingTop: 10,
        }}>
          {t("Cet espace de travail est désactivé")}
        </div>
      )}
    </div>
  );
}
